import { is } from './utils/utils';
import {
	eq,
	excludes,
	excludes_i,
	gt,
	gte,
	imageSizeGT,
	imageSizeLT,
	includes,
	includes_i,
	intersection,
	intersects,
	isInteger,
	lt,
	lte,
	maxLength,
	minLength,
	oneOf,
	required,
	validUSPhoneNum,
} from './utils/functions';
import { DynamicFormEl, ValidatedResult, ValidationFnLookup, ValidationTuple } from './types/DynamicFormTypes';

const validationFns: ValidationFnLookup = {
	required: (value) => ({
		res: required(value),
		msg: 'Required',
	}),
	eq: (given, value) => ({
		res: eq(given, value),
		msg: `Must be ${given}`,
	}),
	gt: (given, value) => ({
		res: gt(given, value),
		msg: `Must be at least ${given}`,
	}),
	lt: (given, value) => ({
		res: lt(given, value),
		msg: `Must be no more than ${given}`,
	}),
	gte: (given, value) => ({
		res: gte(given, value),
		msg: `Must be at least ${given}`,
	}),
	lte: (given, value) => ({
		res: lte(given, value),
		msg: `Must be no more than ${given}`,
	}),
	includes: (given, value) => ({
		res: includes(given, value),
		msg: `Must include '${given}'`,
	}),
	includes_i: (given, value) => ({
		res: includes_i(given, value),
		msg: `Must include '${given}'`,
	}),
	excludes: (given, value) => ({
		res: excludes(given, value),
		msg: `Must not have '${given}'`,
	}),
	excludes_i: (given, value) => ({
		res: excludes_i(given, value),
		msg: `Must not have '${given}'`,
	}),
	oneOf: (given, value) => ({
		res: oneOf(given, value),
		msg: `Must be one of the supplied list`,
	}),
	intersection: (given, value = []) => ({
		res: intersection(given, value),
		msg: `Must include one of the supplied list`,
	}),
	intersects: (given, value = []) => ({
		res: intersects(given, value),
		msg: `Must include one of the supplied list`,
	}),
	minLength: (given, value) => ({
		res: minLength(given, value),
		msg: `Must be at least ${given} characters`,
	}),
	maxLength: (given, value) => ({
		res: maxLength(given, value),
		msg: `Must be no more than ${given} characters`,
	}),
	isInteger: (given) => ({
		res: isInteger(given),
		msg: `Must be an integer`,
	}),
	validUSPhoneNum: (given: string) => ({
		res: validUSPhoneNum(given),
		msg: `Must be valid US phone number`,
	}),
	imageSizeGT: (given: number, file?: File | null) => ({
		res: imageSizeGT(given, file),
		msg: `Must be ${given} bytes`,
	}),
	imageSizeLT: (given: number, file?: File | null) => ({
		res: imageSizeLT(given, file),
		msg: `Must be no more than ${given} bytes`,
	}),
};

export const validateWith = ([fnName, ...rest]: ValidationTuple, value: unknown, customMsg = ''): ValidatedResult => {
	const fn = validationFns[fnName];
	return fn
		? Object.assign(
				(fn as (...args: unknown[]) => ValidatedResult)(...rest, value),
				is('String')(customMsg) && customMsg.length > 0 ? { msg: customMsg } : {}
		  )
		: { res: true, msg: 'Could not find validation' };
};

export const validateInput = (
	value: unknown,
	validations: ValidationTuple[] = [],
	customErrs: string[] = []
): string[] =>
	validations.reduce((acc: string[], loneValidation, idx) => {
		const { res, msg } = validateWith(loneValidation, value, customErrs[idx]);
		return res ? acc : acc.concat(msg);
	}, []);

export const getFieldErrors = (
	fields: DynamicFormEl[],
	/* eslint-disable-next-line  @typescript-eslint/no-explicit-any */
	formData: Record<string, any>,
	passedErrAcc: Record<string, string[]> = {}
): Record<string, string[]> =>
	fields.reduce((errAcc: Record<string, string[]>, { name, validations = [], when = [], customErrs = [] }) => {
		// recurse into when fields
		if (when.length > 0) {
			when.forEach((item) => {
				if (validateWith(item[0], formData[name]).res) {
					getFieldErrors(item[1], formData, errAcc);
				}
			});
		}
		const validationRes = validateInput(formData[name], validations, customErrs);
		return validationRes.length > 0 ? Object.assign(errAcc, { [name]: validationRes }) : errAcc;
	}, passedErrAcc);
